import { createTheme } from '@mui/material'
import { deepPurple, grey, teal } from '@mui/material/colors'
import { darken, lighten } from '@mui/system'
import React from 'react'

import { colors } from 'mui-theme/theme'
import theme from 'theme'

const muiThemeDefault = createTheme()

/**
 * This will add a custom color field to the theme type that allows us to override the default
 * MUI Badge color without impacting the rest of the theme elements (e.g. Buttons, etc.) and
 * without TS errors.
 */
declare module '@mui/material/styles' {
  interface Palette {
    deepPurple50: Palette['primary']
    marketingBlue400: Palette['primary']
    marketingViolet200: Palette['primary']
    marketingViolet650: Palette['primary']
    oceanBlue: Palette['primary']
    orchid: Palette['primary']
    tealAlt: Palette['primary']
    tutorialStep: Palette['primary']
  }

  interface PaletteOptions {
    deepPurple50?: PaletteOptions['primary']
    marketingBlue400?: PaletteOptions['primary']
    marketingViolet200?: PaletteOptions['primary']
    marketingViolet650?: PaletteOptions['primary']
    marketingBlue?: PaletteOptions['primary']
    oceanBlue?: PaletteOptions['primary']
    orchid?: PaletteOptions['primary']
    tealAlt?: PaletteOptions['primary']
    tutorialStep?: PaletteOptions['primary']
  }

  interface TypographyVariants {
    secondaryCaption: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    secondaryCaption?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    secondaryCaption: true
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    tutorialStep: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    deepPurple50: true
    oceanBlue: true
    orchid: true
    tealAlt: true
  }
}

export const muiTheme = createTheme({
  typography: {
    // use our font (Sohne)
    fontFamily: theme.fonts.base,
    // turn off any built-in text transformations (e.g., capitalizing button text)
    allVariants: {
      textTransform: 'none'
    },
    secondaryCaption: {
      fontSize: '0.75rem',
      color: grey['700']
    }
  },
  // TODO discuss if we'll ever want custom theme coloring
  // // docs https://mui.com/material-ui/customization/palette/
  palette: {
    //   primary: {
    //     main: theme.colors.blue[0]
    //     // these should be calculated based on main, but we can override them individually if we want
    //     // light: '#5c5c5c',
    //     // dark: '#0c0c0c',
    //     // contrastText: '#ffffff'
    //   },
    //   secondary: {
    //     main: theme.colors.steel[2]
    //     // light: '#ffffff',
    //     // dark: '#b4b4b4',
    //     // contrastText: '#000000'
    //   },
    //   error: {
    //     main: theme.colors.red[1]
    //     // light: '#cf5263',
    //     // dark: '#650014',
    //     // contrastText: '#ffffff'
    //   },
    //   warning: {
    //     main: theme.colors.orange[0]
    //     // light: '#ffa440',
    //     // dark: '#bb4400',
    //     // contrastText: '#000000'
    //   },
    //   info: {
    //     main: theme.colors.blue[0]
    //     // light: '#5c5c5c',
    //     // dark: '#0c0c0c',
    //     // contrastText: '#ffffff'
    //   },
    //   success: {
    //     main: theme.colors.green[0]
    //     // light: '#67f7b6',
    //     // dark: '#009159',
    //     // contrastText: '#000000'
    //   }
    //   // Used by `getContrastText()` to maximize the contrast between
    //   // the background and the text.
    //   //contrastThreshold: 3,
    //   // Used by the functions below to shift a color's luminance by approximately
    //   // two indexes within its tonal palette.
    //   // E.g., shift from Red 500 to Red 300 or Red 700.
    //   //tonalOffset: 0.2,
    deepPurple50: {
      main: deepPurple['50'],
      light: lighten(deepPurple['50'], 0.2),
      dark: darken(deepPurple['50'], 0.2),
      contrastText: muiThemeDefault.palette.common.black
    },
    marketingBlue400: {
      main: '#3399FF'
    },
    marketingViolet200: {
      main: '#CCC0FD'
    },
    marketingViolet650: {
      main: '#6633CC'
    },
    oceanBlue: {
      main: '#1557b2',
      light: '#e9f4fe',
      dark: '#104184',
      contrastText: muiThemeDefault.palette.common.white
    },
    orchid: {
      main: '#F2CCFF',
      light: '#F2CCFF',
      dark: '#CC33FF',
      contrastText: muiThemeDefault.palette.common.black
    },
    tealAlt: {
      main: teal['A100'],
      light: lighten(teal['A100'], 0.2),
      dark: darken(teal['A100'], 0.2),
      contrastText: muiThemeDefault.palette.common.black
    },
    tutorialStep: {
      main: colors.primary['650'],
      light: colors.primary['200'],
      dark: colors.primary['900'],
      contrastText: muiThemeDefault.palette.common.white
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 960,
      lg: 1152,
      xl: 1400
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
      @font-face {
        font-family: 'Sohne VF';
        src: local(😜), url('/public/fonts/SohneVF-web.woff2') format('woff2');
        font-style: normal;
        font-variation-ligatures: normal;
        font-weight: 400 600;
      }

      @font-face {
        font-family: 'Sohne Mono';
        src: local(😜), url('/public/fonts/SohneMono-light.woff2') format('woff2');
        font-weight: 300;
        font-style: normal;
      }
      
      @media print {
        body {
          /* These overrides preserve the background coloring of various MUI components when printing the window */
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }`
    },
    // TODO staying with default theme for now, but leave this here so we can implement it later
    MuiButton: {
      styleOverrides: {
        root: {
          whiteSpace: 'nowrap',
          textTransform: 'uppercase'
        }
      }
    },
    /**
     * This theme override is to specifically emulate a disabled link style to render like a disabled button
     */
    MuiLink: {
      styleOverrides: {
        root: {
          '&[data-disabled=true]': {
            color: 'rgba(0, 0, 0, 0.26)',
            textDecoration: 'none',
            pointerEvents: 'none'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState: { disabled } }) => ({
          ...(disabled && {
            background: muiThemeDefault.palette.grey['100']
          })
        })
      }
    }
  }
})
